/**
 * Admin Payments API Client
 *
 * Provides methods for managing insurance payments in admin/backoffice context.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

import type {
  AdminPayment,
  AdminPaymentActionFormData,
  PaymentStatus,
  RecordPaymentFormData,
} from "../types/admin-payments.types";

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface AdminPaymentsResponse {
  payments: AdminPayment[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type AdminPaymentsFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: AdminPaymentsFetchFunction = defaultFetch;

export function configureAdminPaymentsApi(
  fetchFn: AdminPaymentsFetchFunction,
): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const adminPaymentsApi = {
  async fetchAll(
    params?: {
      limit?: number;
      offset?: number;
      status?: string;
      paymentMethod?: string;
      search?: string;
    },
    baseUrl = "/api/v1",
  ): Promise<AdminPaymentsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.status) query.set("status", params.status);
    if (params?.paymentMethod)
      query.set("payment_method", params.paymentMethod);
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    const res = await _fetch(`${baseUrl}/admin/payments${qs ? `?${qs}` : ""}`);
    return unwrap<AdminPaymentsResponse>(res);
  },

  async fetchById(
    paymentId: string,
    baseUrl = "/api/v1",
  ): Promise<AdminPayment> {
    const res = await _fetch(`${baseUrl}/admin/payments/${paymentId}`);
    return unwrap<AdminPayment>(res);
  },

  async updatePaymentStatus(
    paymentId: string,
    data: AdminPaymentActionFormData,
    baseUrl = "/api/v1",
  ): Promise<AdminPayment> {
    const statusMap: Record<
      AdminPaymentActionFormData["action"],
      PaymentStatus
    > = {
      mark_completed: "completed",
      mark_failed: "failed",
      refund: "refunded",
      retry: "processing",
    };

    const res = await _fetch(`${baseUrl}/admin/payments/${paymentId}/status`, {
      method: "PATCH",
      body: JSON.stringify({
        status: statusMap[data.action],
        notes: data.notes,
        transaction_ref: data.transactionRef,
        failure_reason: data.failureReason,
      }),
    });
    return unwrap<AdminPayment>(res);
  },

  async recordPayment(
    data: RecordPaymentFormData,
    baseUrl = "/api/v1",
  ): Promise<AdminPayment> {
    const res = await _fetch(`${baseUrl}/admin/payments`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return unwrap<AdminPayment>(res);
  },

  async addNote(
    paymentId: string,
    content: string,
    baseUrl = "/api/v1",
  ): Promise<AdminPayment> {
    const res = await _fetch(`${baseUrl}/admin/payments/${paymentId}/notes`, {
      method: "POST",
      body: JSON.stringify({ content }),
    });
    return unwrap<AdminPayment>(res);
  },
};
